import { Singleton } from '@visiba/instantiation';

interface AnalyticMeta {
	url: string;
	siteId: number;
}

interface AnalyticConfigPresent {
	enabled: true;
	meta: Readonly<AnalyticMeta>;
}

interface AnalyticConfigDisabled {
	enabled: false;
	meta: null;
}

type AnalyticConfig = AnalyticConfigPresent | AnalyticConfigDisabled;

interface Environment {
	api: {
		uri: string | null;
	};
	realtime: {
		host: string | null;
		port: number;
	};
	analyticConfig: AnalyticConfig;
	serverBaseUrl: string;
	cdn: {
		customer: string;
		media: string;
	};
}

@Singleton()
export class HostingService {
	public wsBaseUri: string;
	public apiBaseUri: string;
	public readonly assetPath: string;
	public readonly mediaCdn: string;
	public readonly customerCdn: string;
	public readonly location: string;
	public readonly analyticConfig: AnalyticConfig;

	constructor() {
		// This could be extracted from cortex:react-bundler's config.Visiba module.
		// https://github.com/VisibaCare/visiba-cortex/blob/release/alpha-01/packages/react-bundler/src/config.Visiba.ts
		// But for now it's hardcoded into this service
		const config: Environment =
			process.env.NODE_ENV === 'development'
				? {
						api: {
							uri: process.env.CUSTOM_GLOBAL_ENV['API_URL'] ?? 'https://dev1.vcare.pl',
						},
						realtime: {
							host: process.env.CUSTOM_GLOBAL_ENV['REALTIME_HOST'] ?? 'dev1.vcare.pl',
							port: process.env.CUSTOM_GLOBAL_ENV['REALTIME_PORT'] ?? 443,
						},
						analyticConfig: {
							enabled: false,
							meta: null,
						},
						serverBaseUrl: '',
						cdn: {
							customer: '//customer-cdn.visibacare.com',
							media: '//media-cdn.visibacare.com',
						},
					}
				: {
						api: {
							uri: null,
						},
						realtime: {
							host: null,
							port: 443,
						},
						analyticConfig: this.getAnalyticConfig(),
						serverBaseUrl: '/PublicV3',
						cdn: {
							customer: '//customer-cdn.visibacare.com',
							media: '//media-cdn.visibacare.com',
						},
					};

		this.location = config.api.uri ?? globalThis.location.origin;
		this.assetPath = config.serverBaseUrl;
		this.mediaCdn = config.cdn.media;
		this.customerCdn = config.cdn.customer;
		this.apiBaseUri = `${this.location ?? ''}/api`;
		this.wsBaseUri = `wss://${config.realtime.host ?? location.hostname}:${config.realtime.port}`;
		this.analyticConfig = config.analyticConfig;
	}

	private getAnalyticConfig(): AnalyticConfig {
		let siteId: number;

		if (location.hostname.includes('vcare.pl') || location.hostname.includes('stage-service.visibacare')) {
			siteId = 7; // 7 == New patient web (DEV) siteId in Matomo
		} else {
			siteId = 12; // 12 == New patient web (Prod) siteId in Matomo
		}

		return {
			enabled: true,
			meta: {
				url: '//analytics.visibacare.com/',
				siteId: siteId,
			},
		};
	}
}
